import * as React from 'react';
import { Field, FieldProps } from '../Field';
import { callAll } from '../helper/fp';
import { isNil } from '../helper/typeguard';
import { CprInput, CprInputProps, CprInvalidReasons } from './CprInput';

export const ErrorMessage = {
  InvalidFormat: 'Det indtastede nummer er ikke et gyldigt CPR-nummer.',
  TooOld: 'User is too old',
  TooYoung: 'User is too young',
};

const mapReasonToMessage = (reason: CprInvalidReasons | null) => {
  switch (reason) {
    case 'INVALID_FORMAT':
      return ErrorMessage.InvalidFormat;

    case 'TOO_OLD':
      return ErrorMessage.TooOld;

    case 'TOO_YOUNG':
      return ErrorMessage.TooYoung;

    default:
      return undefined;
  }
};

export type CprFieldProps = {
  /** validation status of the field. If you wish to control this props, pass down null when there is no value */
  status?: 'success' | 'error' | 'warning' | null;
  /** error help text of the field. If you wish to control this props, pass down null when there is no value */
  errorHelpText?: React.ReactNode;
} & CprInputProps &
  Omit<FieldProps, 'status' | 'errorHelpText'>;

class CprFieldComponent extends React.Component<
  CprFieldProps & { innerRef: React.Ref<HTMLInputElement> },
  {
    status: 'success' | 'error' | undefined;
    errorReason: CprInvalidReasons | null;
  }
> {
  state = {
    errorReason: null,
    status: undefined,
  };

  isControl = (propKey: keyof CprFieldProps) => this.props[propKey] !== undefined;

  getStatus = () => (this.isControl('status') ? this.props.status || undefined : this.state.status);

  setStatus = (isValid: boolean | undefined) =>
    !this.isControl('status') &&
    this.setState({
      status: isNil(isValid) ? undefined : isValid ? 'success' : 'error',
    });

  setErrorReason = (errorReason: CprInvalidReasons | null) =>
    !this.isControl('errorHelpText') && this.setState({ errorReason });

  getErrorHelpText = () =>
    this.isControl('errorHelpText')
      ? this.props.errorHelpText || undefined
      : mapReasonToMessage(this.state.errorReason);

  render() {
    const {
      label,
      wrapperClassName,
      status,
      maxWidth,
      compact,
      helpText,
      errorHelpText,
      tooltip,
      tooltipAvoidPositions,
      innerRef,
      onValidityChange,
      onErrorReasonChange,
      alwaysShowHelpText,
      ...inputProps
    } = this.props;

    return (
      <Field
        status={this.getStatus()}
        label={label}
        wrapperClassName={wrapperClassName}
        maxWidth={maxWidth}
        compact={compact}
        alwaysShowHelpText={alwaysShowHelpText}
        helpText={helpText}
        errorHelpText={this.getErrorHelpText()}
        required={inputProps.required}
        tooltip={tooltip}
        tooltipAvoidPositions={tooltipAvoidPositions}
        inputId={inputProps.id}
        renderInput={({ getInputProps }) => (
          <CprInput
            {...getInputProps({
              ...inputProps,
              onErrorReasonChange: callAll(onErrorReasonChange, this.setErrorReason),
              onValidityChange: callAll(onValidityChange, this.setStatus),
              ref: innerRef,
            })}
          />
        )}
      />
    );
  }
}

/**
 * CprField is a composition of `Field` and `CprInput`.
 * If you wish to have more control, you can compose them yourself.
 */
export const CprField = React.forwardRef<HTMLInputElement, CprFieldProps>((props, ref) => (
  <CprFieldComponent {...(props as Exclude<keyof CprFieldProps, 'ref'>)} innerRef={ref} />
));

export default CprField;
